import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { throttle } from 'throttle-debounce';

import { TThunkDispatch, IApplicationState } from '../../../types/redux';
import { TMapBounds } from '../../../types/mapBounds';
import { zoomToPrecision } from '../../../utils/precision';
import { changeBounds } from '../../../actions/mapBounds';
import { useApplicationContext } from '../../ApplicationContext';
import { useMapContext } from '../context';

export const MapBoundsUpdater: React.FC = () => {
  const { config } = useApplicationContext();
  const { map } = useMapContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const storeMapBounds = useSelector<IApplicationState, TMapBounds>(state => state.mapBounds);
  const [currentMapBounds, setCurrentMapBounds] = React.useState<TMapBounds>();

  const handleBoundsChange = React.useCallback(
    throttle(300, () => {
      const zoom = map.getZoom();

      const nextBounds: TMapBounds = {
        bounds: map.getBounds(),
        center: map.getCenter(),
        precision: zoomToPrecision(config, zoom),
        zoom,
      };

      setCurrentMapBounds(nextBounds);
      dispatch(changeBounds(nextBounds));
    }),
    [map, dispatch],
  );

  React.useEffect(() => {
    map.events.add('boundschange', handleBoundsChange);

    handleBoundsChange();

    return () => {
      map.events.remove('boundschange', handleBoundsChange);
    };
  }, [map, handleBoundsChange]);

  React.useEffect(() => {
    if (storeMapBounds !== currentMapBounds) {
      if (storeMapBounds.bounds) {
        map.setBounds(storeMapBounds.bounds).then(() => handleBoundsChange());
      } else if (storeMapBounds.center && storeMapBounds.zoom) {
        map.setCenter(storeMapBounds.center, storeMapBounds.zoom).then(() => handleBoundsChange());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeMapBounds]);

  return null;
};
